import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mitteilungen } from '../api/apiClient';
import { formatDateiGroesse } from '../utils/format';

/**
 * Anhangliste einer Mitteilung: hochgeladene Dokumente und Verweise.
 *
 * Externe Verweise oeffnen in einem neuen Tab; rel="noreferrer" verhindert,
 * dass die Zielseite ueber window.opener auf die Anwendung zugreift.
 * Interne Pfade (beginnen mit "/") navigieren per Router im selben Tab;
 * `onNavigate` erlaubt z. B. einem Modal, sich dabei zu schliessen.
 */
function MitteilungAnhaenge({ anhaenge, onFehler, onNavigate }) {
  const [ladend, setLadend] = useState(null);

  const liste = Array.isArray(anhaenge) ? anhaenge : [];
  if (liste.length === 0) return null;

  const handleDownload = async (anhang) => {
    setLadend(anhang.anhangId);
    try {
      await Mitteilungen.downloadAnhang(anhang.anhangId, anhang.originalerDateiname);
    } catch (e) {
      onFehler?.(`Download fehlgeschlagen: ${e.message}`);
    } finally {
      setLadend(null);
    }
  };

  return (
    <div className="space-y-1.5">
      <p className="section-label">Anhänge</p>
      <ul className="space-y-1">
        {liste.map((a) => (
          <li key={a.anhangId}>
            {a.typ === 'link' && istInternerPfad(a.url) ? (
              <Link
                to={a.url}
                onClick={() => onNavigate?.()}
                className="flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm text-brand-600 hover:bg-slate-50 no-underline"
              >
                <LinkIcon className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
                <span className="min-w-0">
                  <span className="font-medium underline">{a.bezeichnung || a.url}</span>
                </span>
              </Link>
            ) : a.typ === 'link' ? (
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm text-brand-600 hover:bg-slate-50 no-underline"
              >
                <LinkIcon className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
                <span className="min-w-0">
                  <span className="font-medium underline">{a.bezeichnung || a.url}</span>
                  <span className="block truncate text-xs text-slate-400">{a.url}</span>
                </span>
              </a>
            ) : (
              <button
                type="button"
                onClick={() => handleDownload(a)}
                disabled={ladend === a.anhangId}
                className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                <FileIcon className="h-4 w-4 shrink-0 mt-0.5 text-slate-400" />
                <span className="min-w-0">
                  <span className="font-medium">{a.bezeichnung || a.originalerDateiname}</span>
                  <span className="block text-xs text-slate-400">
                    {a.bezeichnung && a.originalerDateiname ? `${a.originalerDateiname} · ` : ''}
                    {formatDateiGroesse(a.dateigroesse)}
                    {ladend === a.anhangId ? ' · wird geladen…' : ''}
                  </span>
                </span>
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Pfad innerhalb der Anwendung, z. B. "/allgemein/kalender" ("//host" ist extern). */
export function istInternerPfad(url) {
  return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\');
}

function FileIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function LinkIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7L12 19" />
    </svg>
  );
}

export default MitteilungAnhaenge;
